import { colors } from "@rubi/ui";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
  type Layout,
} from "react-resizable-panels";
import { useSearchParams } from "react-router-dom";

import { DisplayAlertWrapper } from "./DisplayAlertWrapper";

import "@/components/alerts/alerts.scss";
import { ALERT_STATUS } from "@/components/alerts/constants";
import { SingleAlertProvider } from "@/components/alerts/context";

/**
 * One bulletin's share of the drawer.
 *
 * The class marks it for the expanded media viewer, which fills the pane a photo was opened from
 * rather than the whole drawer -- side by side that leaves the other bulletin readable and its own
 * photo one click away. The viewer is an `inset: 0` overlay, so the pane is what has to be
 * positioned; it must also not be the scroller, or the overlay would sit above the visible area
 * once the bulletin is scrolled. `.alert-container` inside scrolls instead -- it is `h-100`, so
 * this stays exactly as tall as the pane.
 */
const ALERT_PANE_CLASS = "alert-pane";
const ALERT_PANE_STYLE = {
  height: "100%",
  width: "100%",
  minWidth: 0,
  position: "relative",
  overflow: "hidden",
} as const;

const PANEL_LAYOUT_STORAGE_KEY = "rubi-alert-display-alert-drawer-layout";
const DEFAULT_COMPARE_LAYOUT: Layout = {
  compare: 50,
  main: 50,
};

interface DisplayAlertDrawerProps {
  mode: ALERT_STATUS;
  queryNextPage?: () => void;
}

const getSavedCompareLayout = (): Layout => {
  try {
    const savedLayout = localStorage.getItem(PANEL_LAYOUT_STORAGE_KEY);
    if (!savedLayout) return DEFAULT_COMPARE_LAYOUT;

    const parsedLayout = JSON.parse(savedLayout) as Layout;
    if (
      typeof parsedLayout.compare === "number" &&
      typeof parsedLayout.main === "number"
    ) {
      return parsedLayout;
    }
  } catch {
    // Fall back to the current equal split if the saved layout is unavailable.
  }

  return DEFAULT_COMPARE_LAYOUT;
};

const saveCompareLayout = (layout: Layout) => {
  localStorage.setItem(PANEL_LAYOUT_STORAGE_KEY, JSON.stringify(layout));
};

export const DisplayAlertDrawer = ({
  mode,
  queryNextPage = () => {},
}: DisplayAlertDrawerProps) => {
  const [searchParams] = useSearchParams();
  const bulletinId = searchParams.get("bulletinId");
  const compareBulletinId = searchParams.get("compareBulletinId");
  const hasCompareBulletin =
    Boolean(compareBulletinId) && compareBulletinId !== bulletinId;

  if (!hasCompareBulletin) {
    return (
      <div className={ALERT_PANE_CLASS} style={ALERT_PANE_STYLE}>
        <SingleAlertProvider>
          <DisplayAlertWrapper
            display_type={"main"}
            mode={mode}
            queryNextPage={queryNextPage}
          />
        </SingleAlertProvider>
      </div>
    );
  }

  return (
    <PanelGroup
      id="display-alert-drawer-panels"
      orientation="horizontal"
      defaultLayout={getSavedCompareLayout()}
      onLayoutChanged={saveCompareLayout}
      style={{
        height: "100%",
        width: "100%",
      }}
    >
      <Panel id="compare" minSize="25%" defaultSize="50%">
        <div className={ALERT_PANE_CLASS} style={ALERT_PANE_STYLE}>
          <SingleAlertProvider>
            <DisplayAlertWrapper display_type={"compare"} mode={mode} />
          </SingleAlertProvider>
        </div>
      </Panel>
      <PanelResizeHandle
        style={{
          width: 6,
          background: colors.divider,
          cursor: "col-resize",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: 2,
            height: 40,
            background: colors.text.disabled,
            borderRadius: 4,
          }}
        />
      </PanelResizeHandle>
      <Panel id="main" minSize="25%" defaultSize="50%">
        <div className={ALERT_PANE_CLASS} style={ALERT_PANE_STYLE}>
          <SingleAlertProvider>
            <DisplayAlertWrapper
              display_type={"main"}
              mode={mode}
              queryNextPage={queryNextPage}
            />
          </SingleAlertProvider>
        </div>
      </Panel>
    </PanelGroup>
  );
};
